// src/components/PlayersTable.jsx
import { PHASES, ROLES } from "../utils/constants.js";

export default function PlayersTable({ state, me }) {
  const phase     = state.phase;
  const players   = state.players || [];
  const confirmed = state.confirmedVotes || [];

  // Day vote tally
  const dayTally = {};
  if (phase === PHASES.DAY) {
    players.filter((p) => p.alive).forEach((p) => {
      const t = (state.dayVotes || {})[p.id];
      if (t) dayTally[t] = (dayTally[t] || 0) + 1;
    });
  }

  return (
    <div style={{ border: "1px solid rgba(201,168,76,0.13)", borderRadius: 3, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", padding: "0.45rem 0.9rem", background: "rgba(201,168,76,0.05)", fontSize: "0.58rem", letterSpacing: "0.2em", color: "var(--gold-dim)", textTransform: "uppercase" }}>
        <span>Player</span>
        <span>Status</span>
      </div>

      {/* Rows */}
      {players.map((p) => {
        const isMe         = p.id === me.id;
        const totalVotes   = dayTally[p.id] || 0;

        // Voters on this player (day phase only)
        const dayVoters = phase === PHASES.DAY
          ? Object.entries(state.dayVotes || {})
              .filter(([, t]) => t === p.id)
              .map(([v]) => ({
                name:      players.find((x) => x.id === v)?.name || "?",
                confirmed: confirmed.includes(v),
              }))
          : [];

        // Mafia voters visible only to mafia at night
        const nightVoters = phase === PHASES.NIGHT && me.role === ROLES.MAFIA
          ? Object.entries(state.mafiaVotes || {})
              .filter(([, t]) => t === p.id)
              .map(([v]) => players.find((x) => x.id === v)?.name || "?")
          : [];

        // Doctor voters visible only to doctors in save phase
        const saveVoters = phase === PHASES.SAVE && me.role === ROLES.DOCTOR
          ? Object.entries(state.doctorVotes || {})
              .filter(([, t]) => t === p.id)
              .map(([v]) => players.find((x) => x.id === v)?.name || "?")
          : [];

        return (
          <div
            key={p.id}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              alignItems: "center",
              padding: "0.55rem 0.9rem",
              borderTop: "1px solid rgba(201,168,76,0.06)",
              background: isMe ? "rgba(201,168,76,0.03)" : "transparent",
              opacity: p.alive ? 1 : 0.35,
              gap: "0.5rem",
            }}
          >
            <div style={{ fontSize: "0.86rem", display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}>
              {/* Alive indicator */}
              <span style={{ color: p.alive ? "#4db84d" : "#555", fontSize: "0.7rem", flexShrink: 0 }}>
                {p.alive ? "●" : "✕"}
              </span>

              {/* Name */}
              <span style={{ textDecoration: p.alive ? "none" : "line-through" }}>{p.name}</span>

              {/* Badges */}
              {isMe && <Badge color="rgba(201,168,76,0.4)" border="rgba(201,168,76,0.3)">YOU</Badge>}
              {p.isHost && <Badge color="rgba(201,168,76,0.3)" border="rgba(201,168,76,0.2)">HOST</Badge>}

              {/* Revealed role (only when roleRevealed=true and dead) */}
              {!p.alive && p.roleRevealed && p.role === ROLES.MAFIA && (
                <Badge color="#e05555" border="rgba(139,26,26,0.5)">MAFIA</Badge>
              )}

              {/* Vote pips - day */}
              {dayVoters.length > 0 && (
                <div style={{ display: "flex", gap: "0.2rem", flexWrap: "wrap" }}>
                  {dayVoters.map((v, i) => (
                    <span key={i} style={{ fontSize: "0.58rem", padding: "0.1rem 0.28rem", borderRadius: 2, background: v.confirmed ? "rgba(201,168,76,0.18)" : "rgba(201,168,76,0.07)", border: `1px solid ${v.confirmed ? "rgba(201,168,76,0.4)" : "rgba(201,168,76,0.18)"}`, color: "var(--gold)" }}>
                      {v.name}{v.confirmed ? " ✓" : ""}
                    </span>
                  ))}
                </div>
              )}

              {/* Vote pips - night */}
              {nightVoters.map((n, i) => (
                <span key={i} style={{ fontSize: "0.58rem", padding: "0.1rem 0.28rem", borderRadius: 2, background: "rgba(139,26,26,0.3)", border: "1px solid rgba(139,26,26,0.5)", color: "#e05555" }}>{n}</span>
              ))}

              {/* Vote pips - save */}
              {saveVoters.map((n, i) => (
                <span key={i} style={{ fontSize: "0.58rem", padding: "0.1rem 0.28rem", borderRadius: 2, background: "rgba(26,92,92,0.3)", border: "1px solid rgba(26,92,92,0.5)", color: "#4db8b8" }}>{n}</span>
              ))}
            </div>

            {/* Right column */}
            <div style={{ fontSize: "0.75rem", display: "flex", alignItems: "center", gap: "0.4rem" }}>
              {!p.alive && <span>☠</span>}
              {phase === PHASES.DAY && totalVotes > 0 && (
                <span style={{ color: "var(--gold)", fontFamily: "'Playfair Display', serif", fontWeight: 700 }}>
                  {totalVotes}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Badge({ color, border, children }) {
  return (
    <span style={{ fontSize: "0.5rem", letterSpacing: "0.1em", color, border: `1px solid ${border}`, padding: "0.08rem 0.28rem", borderRadius: 1, flexShrink: 0 }}>
      {children}
    </span>
  );
}
